<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

const { t } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const route = useRoute();
const { trackEvent } = useAnalytics();

// Don't show on admin/comingsoon where this would be intrusive or irrelevant.
const excludedPaths = ["/admin", "/comingsoon"];
const isExcluded = computed(() =>
  excludedPaths.some((p) => route.path.startsWith(p) || route.path.startsWith(`/ru${p}`) || route.path.startsWith(`/ka${p}`))
);

// User can dismiss the bar per session (sessionStorage, resets next visit).
const dismissed = ref(false);

// Reveal the bar only after the user has scrolled a bit — keeps the hero
// uncluttered on first view. `scrollY > 300` is past most hero sections.
const visible = ref(false);

function handleScroll() {
  visible.value = window.scrollY > 300;
}

function dismiss() {
  dismissed.value = true;
  try {
    sessionStorage.setItem("sticky-booking-dismissed", "1");
  } catch {
    // sessionStorage may be disabled in private mode; silent fallback.
  }
  trackEvent("booking_widget_dismiss", { placement: "sticky" });
}

onMounted(() => {
  try {
    if (sessionStorage.getItem("sticky-booking-dismissed") === "1") {
      dismissed.value = true;
    }
  } catch {
    // no-op
  }
  window.addEventListener("scroll", handleScroll, { passive: true });
  handleScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});

const whatsappUrl = computed(() => {
  const phone = (config.public.whatsappNumber as string) || "+995505556117";
  const digits = phone.replace(/\D/g, "");
  const text = encodeURIComponent(t("stickyBooking.message"));
  return `https://wa.me/${digits}?text=${text}`;
});

const telegramUrl = computed(() => (config.public.telegramLink as string) || "");

function trackCtaClick() {
  trackEvent("booking_widget_click", { placement: "sticky", channel: "whatsapp" });
}

function trackTelegramClick() {
  trackEvent("booking_widget_click", { placement: "sticky", channel: "telegram" });
}

function trackApartmentsClick() {
  trackEvent("booking_widget_click", { placement: "sticky", channel: "apartments" });
}

const show = computed(() => visible.value && !dismissed.value && !isExcluded.value);
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
  >
    <aside
      v-if="show"
      role="complementary"
      :aria-label="t('stickyBooking.line')"
      class="fixed inset-x-0 bottom-0 z-[999] pointer-events-none"
    >
      <div
        class="pointer-events-auto mx-auto max-w-5xl bg-slate-900/95 shadow-[0_-8px_30px_rgb(0,0,0,0.12)] backdrop-blur-md md:mb-4 md:rounded-full md:shadow-2xl dark:bg-slate-950/95"
        style="padding-bottom: env(safe-area-inset-bottom)"
      >
        <div class="flex items-center gap-3 px-4 py-3 sm:gap-4 sm:px-5 md:px-6">
          <!-- Copy (line): hidden on narrow phones to give room for buttons -->
          <p
            class="hidden min-w-0 flex-1 font-body text-sm leading-snug text-white/90 sm:block"
          >
            {{ t("stickyBooking.line") }}
          </p>

          <!-- Apartments secondary link: tablet+ -->
          <NuxtLink
            :to="localePath('/')"
            class="hidden shrink-0 font-body text-xs font-semibold uppercase tracking-[0.18em] text-amber-300 transition-colors hover:text-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded-sm md:inline-flex"
            @click="trackApartmentsClick"
          >
            {{ t("stickyBooking.apartmentsLink") }}
          </NuxtLink>

          <!-- Primary WhatsApp CTA -->
          <a
            :href="whatsappUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 font-body text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-sm transition-all hover:bg-emerald-400 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 sm:flex-none sm:px-6"
            @click="trackCtaClick"
          >
            <Icon name="mdi-whatsapp" class="size-5 shrink-0" aria-hidden="true" />
            <span class="hidden sm:inline">{{ t("stickyBooking.cta") }}</span>
            <span class="sm:hidden">{{ t("stickyBooking.ctaShort") }}</span>
          </a>

          <!-- Telegram secondary CTA -->
          <a
            v-if="telegramUrl"
            :href="telegramUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-sky-500 px-5 py-2.5 font-body text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-sm transition-all hover:bg-sky-400 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 sm:flex-none sm:px-6"
            @click="trackTelegramClick"
          >
            <Icon name="ic:baseline-telegram" class="size-5 shrink-0" aria-hidden="true" />
            <span class="hidden sm:inline">{{ t("stickyBooking.telegramCta") }}</span>
            <span class="sm:hidden">{{ t("stickyBooking.telegramCtaShort") }}</span>
          </a>

          <!-- Dismiss — small close button, last tab stop -->
          <button
            type="button"
            :aria-label="t('stickyBooking.close')"
            class="shrink-0 rounded-full p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            @click="dismiss"
          >
            <Icon name="lucide:x" class="size-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </aside>
  </Transition>
</template>
